/**
 * PostPanel — M2.4 fan-out posting.
 *
 * No more single-select dropdown. The "Post to Instagram" button posts to
 * **every** active linked IG account of the content's persona, in one call.
 * The API resolves the target list server-side and dispatches a single
 * `PostContentToAllAccountsWorkflow` parent (which fans out to children).
 *
 * Zero-state: when the persona has no eligible accounts linked, the panel
 * collapses to a "Link an account" CTA pointing at the persona detail page.
 */

import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';
import type { Account } from '@/types/account';
import type { ContentStatus } from '@/types/content';

export function PostPanel({
  personaId,
  contentId,
  contentStatus,
}: {
  personaId: string;
  contentId: string;
  contentStatus: ContentStatus;
}) {
  const navigate = useNavigate();
  const accountsQ = useQuery({
    queryKey: ['persona', personaId, 'accounts'],
    queryFn: () => api.listPersonaAccounts(personaId),
  });
  const accounts: Account[] = accountsQ.data ?? [];
  // Same filter as the server-side `list_postable_accounts` activity: IG,
  // active, has credentials. Kept in sync with apps/worker-python/.../accounts.py.
  const eligible = accounts.filter(
    (a) => a.platform === 'instagram' && a.status === 'active' && a.has_credentials,
  );
  // Exact caption the post will carry (hook / body / next action /
  // disclosure / hashtags) so the operator sees the AI disclosure before Post.
  const captionQ = useQuery({
    queryKey: ['content', contentId, 'caption'],
    queryFn: () => api.getContentCaption(contentId),
    enabled: contentStatus === 'approved',
  });

  const postM = useMutation({
    // Empty body → server fans out to every eligible account for the persona.
    mutationFn: () => api.postContent(contentId),
    onSuccess: (res) => {
      toast.success(
        eligible.length > 1
          ? `Posting to ${eligible.length} accounts; watching the run.`
          : 'Posting started; watching the run.',
      );
      void navigate({ to: '/runs/$workflowId', params: { workflowId: res.workflow_id } });
    },
    onError: (err) => toast.error('Post failed', { description: String(err) }),
  });

  const alreadyPublished = contentStatus === 'published';
  const notApproved = contentStatus !== 'approved';
  const noAccount = !accountsQ.isLoading && eligible.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Publish</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {alreadyPublished && (
          <p className="text-sm text-muted-foreground">
            This content has been published. Per-account URLs appear in the Posts section below.
          </p>
        )}
        {!alreadyPublished && notApproved && (
          <p className="text-sm text-muted-foreground">
            Approve this content before posting. Posting is gated server-side too.
          </p>
        )}
        {!alreadyPublished && noAccount && (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-muted-foreground">
              No Instagram account linked to this persona.
            </p>
            <Link to="/personas/$personaId" params={{ personaId }}>
              <Button variant="outline" size="sm">
                <ExternalLink className="h-3.5 w-3.5" />
                Link an account
              </Button>
            </Link>
          </div>
        )}
        {!alreadyPublished && !notApproved && captionQ.data && (
          <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    Caption as posted
                  </span>
                  <Badge tone={captionQ.data.disclose_ai ? 'success' : 'warning'}>
                    {captionQ.data.disclose_ai ? 'AI label on' : 'AI label off'}
                  </Badge>
                </div>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/30 px-3 py-2 font-sans text-xs leading-relaxed">
                  {captionQ.data.caption}
                </pre>
                {captionQ.data.disclose_ai && (
                  <p className="text-xs text-muted-foreground">
                    Instagram's "Add AI label" is toggled on before sharing. If it can't be
                    confirmed, the post is aborted rather than published undisclosed.
                  </p>
                )}
          </div>
        )}
        {!alreadyPublished && !notApproved && !noAccount && (
          <div className="space-y-3">
            <div className="space-y-1">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Posting to {eligible.length} account{eligible.length === 1 ? '' : 's'}
              </span>
              <ul className="flex flex-wrap gap-1.5">
                {eligible.map((a) => (
                  <li key={a.account_id}>
                    <Badge tone="neutral">@{a.handle}</Badge>
                  </li>
                ))}
              </ul>
            </div>
            <Button onClick={() => postM.mutate()} disabled={postM.isPending}>
              <Send className="h-4 w-4" />
              {postM.isPending
                ? 'Starting…'
                : eligible.length > 1
                  ? `Post to ${eligible.length} Instagram accounts`
                  : 'Post to Instagram'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
